import { MouseEvent } from 'react';
import cn from 'classnames';
import { components } from '@api/api';
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import Icon from '@components/Icon/Icon';
import ActionIcon from '@components/ActionIcon';
import {
    IconCheck,
    IconGear,
    IconCaretDoubleDown,
    IconFileSearch,
} from '@icons';
import { FCX } from "@datassential/platform-ui-lib";

type FilterWord = components['schemas']['FilterWord'];

export type KeywordCellProps = {
    word: FilterWord | undefined;
    isSelected: boolean;
    isSelectable: boolean;
    onSelect: (wordName: string) => void;
    onSettingsClick: (e: MouseEvent<HTMLDivElement>, word: FilterWord) => void;
    onDrillClick: (word: FilterWord) => void;
    onMenuExamplesClick: (word: FilterWord) => void;
};

const KeywordCell: FCX<KeywordCellProps> = ({
        word,
        isSelected,
        isSelectable,
        onSelect,
        onSettingsClick,
        onDrillClick,
        onMenuExamplesClick,
}) => {
    const { resetSearch } = useAiSearchSettingsContext();

    if (!word) return null;

    const hasWordExtensions = (word.wordExtensions?.length ?? 0) > 0;

    const handleRowClick = () => {
        if (isSelectable && word.name) {
            onSelect(word.name);
        }
    };

    const handleSettingsClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onSettingsClick(e, word);
    };

    const handleDrillClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onDrillClick(word);
        resetSearch();
    };

    const handleMenuExamplesClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onMenuExamplesClick(word);
    };

    return (
        <div
            className={cn(
                'TopTrendsKeywordCell',
                isSelectable && 'is-selectable',
            )}
            onClick={handleRowClick}
        >
            <div
                className={cn(
                    'TopTrendsKeywordCell__content',
                    !word.isTag && 'is-light',
                    word.isTag && 'tag-label',
                )}
            >
                <div
                    className={cn(
                        'TopTrendsKeywordCell__checkbox-container',
                        isSelectable && 'TopTrendsKeywordCell__checkbox-container--visible',
                    )}
                >
                    <span
                        className={cn(
                            'TopTrendsKeywordCell__checkbox-icon',
                            isSelected && 'TopTrendsKeywordCell__checkbox-icon--checked',
                        )}
                    >
                        <Icon type="small">
                            <IconCheck/>
                        </Icon>
                    </span>
                </div>

                <span className="TopTrendsKeywordCell__name">
                    {word.name}
                </span>
            </div>

            <div className="TopTrendsKeywordCell__actions">
                <div className="TopTrendsKeywordCell__action" onClick={handleSettingsClick}>
                    <ActionIcon
                        isDisabled={!hasWordExtensions}
                        tooltip="includes / excludes"
                    >
                        <IconGear width={14}/>
                    </ActionIcon>
                </div>
                <div className="TopTrendsKeywordCell__action" onClick={handleDrillClick}>
                    <ActionIcon tooltip="drill">
                        <IconCaretDoubleDown width={12}/>
                    </ActionIcon>
                </div>
                <div className="TopTrendsKeywordCell__action" onClick={handleMenuExamplesClick}>
                    <ActionIcon tooltip="menu examples">
                        <IconFileSearch width={20}/>
                    </ActionIcon>
                </div>
            </div>
        </div>
    );
};

export default KeywordCell;
